import { apiService } from '../api/api-client';
import { CustodianItem } from '@/types';

/**
 * Custodian registry write-service (LR-441).
 *
 * READ access lives in `trades-service` (getCustodians / getAllCustodians) and
 * feeds the trade-entry dropdown — left untouched here. This module only adds
 * the create / update calls backing the Custodians register tab.
 */
export interface CustodianFormData {
  name: string;
  shortCode: string;
  accountNumber: string;
  isActive: boolean;
}

/** Create response: the create serializer returns every custodian field. */
export type CustodianCreateResult = CustodianItem;

/**
 * Update (PATCH) response: the update serializer omits `short_code` from its
 * `fields` (it is the immutable business key), so the response carries every
 * field except `short_code` — the type reflects that.
 *
 * LR-468: an `account_number` Backoffice does not know is rejected pre-save with
 * HTTP 400 (field error), so a successful write no longer carries any advisory.
 */
export type CustodianUpdateResult = Omit<CustodianItem, 'short_code'>;

/** Options controlling how a custodian PATCH payload is built. */
export interface CustodianUpdateOptions {
  /**
   * LR-468: only send `account_number` when it actually changed. The backend
   * runs a *blocking* Backoffice lookup on any present `account_number` (one BO
   * returns a definitive 404 for is rejected with HTTP 400) but skips it
   * entirely when the field is absent from the PATCH. Omitting an unchanged
   * value lets an operator still edit `name` / `is_active` of a custodian whose
   * base account has since gone 404 in Backoffice — without re-triggering the
   * block on every save.
   */
  accountNumberChanged: boolean;
}

export const custodianService = {
  /**
   * Create a custodian. New custodians are active by default.
   */
  async createCustodian(data: CustodianFormData): Promise<CustodianCreateResult> {
    return apiService.post<CustodianCreateResult>('/references/custodians/', {
      name: data.name,
      short_code: data.shortCode,
      account_number: data.accountNumber ?? '',
      is_active: true,
    });
  },

  /**
   * Update a custodian (PATCH). `short_code` is immutable: it is omitted so the
   * backend leaves the unique business key unchanged (mirrors counterparty
   * `client_id`). The update serializer also omits `short_code` from its
   * `fields`, so the PATCH response carries every field except `short_code` —
   * the return type reflects that (caller re-fetches the full row from the list).
   *
   * `account_number` is sent only when it changed (see {@link CustodianUpdateOptions});
   * an unchanged value is omitted so the backend skips the blocking Backoffice
   * lookup, keeping an edit of `name` / `is_active` possible even when the base
   * account has since gone 404 in Backoffice.
   */
  async updateCustodian(
    id: number,
    data: CustodianFormData,
    { accountNumberChanged }: CustodianUpdateOptions,
  ): Promise<CustodianUpdateResult> {
    const payload: { name: string; is_active: boolean; account_number?: string } = {
      name: data.name,
      is_active: data.isActive,
    };
    if (accountNumberChanged) {
      payload.account_number = data.accountNumber ?? '';
    }
    return apiService.patch<CustodianUpdateResult>(`/references/custodians/${id}/`, payload);
  },
};
